import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Text,
  Input,
  FormControl,
  Tooltip,
  IconButton,
  Flex,
  SimpleGrid,
  useColorMode,
} from "@chakra-ui/react";
import {
  ArrowForwardIcon,
  CheckIcon,
  CloseIcon,
  EmailIcon,
} from "@chakra-ui/icons";
import {
  X,
  Package,
  TruckIcon,
  CheckCircle2,
  Mail,
  Search,
  ChartLine,
} from "lucide-react";
import { trackNewsletterSubscribed } from "../../utils/analytics";
import { getCurrentColor } from "../../constants";
import useHttp from "../../hooks/useHttp";

const Newsletter = ({ isPopup }) => {
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [wasPopupClosed] = useState(
    () => localStorage.getItem("newsletterClosed") === "true",
  );
  const [wasUserSubscribed] = useState(
    () => localStorage.getItem("isSubscribed") === "true",
  );

  const { colorMode } = useColorMode();
  const { postNewsLetter } = useHttp();

  const [subscribeStatus, setSubscribeStatus] = useState(null);

  useEffect(() => {
    if (!isPopup) return;

    if (wasPopupClosed || wasUserSubscribed) {
      setShowNewsletter(false);
      return;
    }

    const timer = setTimeout(() => {
      setShowNewsletter(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [isPopup, wasPopupClosed, wasUserSubscribed]);

  const handleSubscribe = (e, emailValue) => {
    e.preventDefault();
    const trimmed = (emailValue || "").trim();

    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    setEmailError("");
    setIsSubmitting(true);

    postNewsLetter({ email: trimmed })
      .then((res) => {
        setSubscribeStatus(res);

        if (res?.status === "success") {
          localStorage.setItem("isSubscribed", "true");
          trackNewsletterSubscribed();
        }
      })
      .catch((err) => setSubscribeStatus(err?.response?.data || err))
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleDontShowAgain = () => {
    localStorage.setItem("newsletterClosed", "true");
    setShowNewsletter(false);
  };

  return (
    <>
      {isPopup && showNewsletter && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={300}
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={4}
          onClick={handleDontShowAgain}
        >
          <Box
            position="absolute"
            inset={0}
            bg={getCurrentColor(
              colorMode,
              "rgba(0,0,0,0.6)",
              "rgba(11,16,24,0.75)",
            )}
            backdropFilter="blur(2px)"
          />

          <Box
            position="relative"
            bg={getCurrentColor(colorMode, "whiteC.100", "grayC.1700")}
            border={"1px solid"}
            borderColor={getCurrentColor(colorMode, "grayC.1200", "grayC.2400")}
            borderRadius="12px"
            boxShadow="2xl"
            w="100%"
            maxW="560px"
            maxH="calc(100dvh - 2rem)"
            overflowY="auto"
            p={{ base: 6, md: 8 }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              aria-label="Close popup"
              icon={<X size={18} />}
              onClick={handleDontShowAgain}
              position="absolute"
              right={4}
              top={4}
              variant="ghost"
              color={getCurrentColor(colorMode, "grayC.200", "grayC.1600")}
              _hover={{
                bg: getCurrentColor(colorMode, "grayC.1100", "grayC.2000"),
              }}
            />

            <Flex justifyContent="center" mb={5}>
              <Box position="relative">
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={3}
                  textAlign="center"
                >
                  <Box
                    w="80px"
                    h="80px"
                    borderRadius="full"
                    bg={getCurrentColor(colorMode, "blue.50", "blue.900")}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box
                      color={getCurrentColor(colorMode, "blue.400", "blue.300")}
                      display="flex"
                    >
                      <TruckIcon size={36} />
                    </Box>
                  </Box>
                </Box>
                <Flex
                  position="absolute"
                  bottom="-4px"
                  right="-4px"
                  w="30px"
                  h="30px"
                  borderRadius="full"
                  bg={getCurrentColor(colorMode, "#dcfce7", "#14532d")}
                  alignItems="center"
                  justifyContent="center"
                >
                  <CheckCircle2
                    size={16}
                    color={getCurrentColor(colorMode, "#16a34a", "#4ade80")}
                  />
                </Flex>
              </Box>
            </Flex>

            <Text
              textAlign="center"
              fontSize={{ base: "xl", md: "2xl" }}
              fontWeight="700"
              color={getCurrentColor(colorMode, "blackC.200", "whiteC.100")}
              mb={1}
            >
              We&apos;re Not Like the Others
            </Text>
            <Text
              textAlign="center"
              color={getCurrentColor(colorMode, "blueC.400", "blueC.100")}
              fontWeight="700"
              fontSize={{ base: "sm", md: "md" }}
              mb={4}
            >
              Shipping is Included in Total CPR
            </Text>

            <Text
              textAlign="center"
              color={getCurrentColor(colorMode, "grayC.200", "grayC.1600")}
              fontSize={{ base: "sm", md: "md" }}
              mb={5}
            >
              No more guessing shipping costs or filtering by "free shipping"
              only. BulletScout includes shipping in every cost-per-round
              calculation so you can compare all vendors from the start.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={6}>
              {[
                { icon: TruckIcon, text: "Shop CPR with Shipping" },
                { icon: Search, text: "Compare all vendors instantly" },
                { icon: Package, text: "Shop ammo by product" },
                { icon: ChartLine, text: "Track price history & trends" },
              ].map(({ icon: Icon, text }) => (
                <Flex key={text} alignItems="center" gap={2.5}>
                  <Flex
                    w="20px"
                    h="20px"
                    borderRadius="full"
                    bg={getCurrentColor(colorMode, "#dcfce7", "#14532d")}
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Icon
                      size={12}
                      color={getCurrentColor(colorMode, "#16a34a", "#4ade80")}
                    />
                  </Flex>
                  <Text
                    fontSize="sm"
                    color={getCurrentColor(
                      colorMode,
                      "blackC.200",
                      "whiteC.100",
                    )}
                  >
                    {text}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>

            <Box
              borderTop="1px solid"
              borderColor={getCurrentColor(
                colorMode,
                "grayC.1200",
                "grayC.2400",
              )}
              pt={5}
              mb={4}
            >
              <Flex alignItems="center" gap={2} justifyContent="center" mb={2}>
                <Mail
                  size={14}
                  color={getCurrentColor(colorMode, "#2563eb", "#60a5fa")}
                />
                <Text
                  fontSize="sm"
                  fontWeight="700"
                  color={getCurrentColor(colorMode, "blackC.200", "whiteC.100")}
                >
                  Sign up for our Newsletter
                </Text>
              </Flex>
              <Text
                fontSize="xs"
                color={getCurrentColor(colorMode, "grayC.200", "grayC.1600")}
                textAlign="center"
                mb={3}
              >
                Get price drop alerts, exclusive deals, and inventory updates
                delivered to your inbox.
              </Text>

              {subscribeStatus?.status === "success" ? (
                <Flex
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap={2}
                  py={2}
                >
                  <CheckIcon fontSize="32px" color="green.500" />
                  <Text color="green.500" fontSize="md" textAlign="center">
                    {subscribeStatus?.message || "You are subscribed!"}
                  </Text>
                </Flex>
              ) : (
                <form onSubmit={(e) => handleSubscribe(e, email)}>
                  <Box position="relative" mb={2}>
                    <Input
                      type="email"
                      placeholder="Your email address"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError("");
                      }}
                      bg={getCurrentColor(
                        colorMode,
                        "whiteC.100",
                        "grayC.2000",
                      )}
                      border={"0.5px solid"}
                      borderColor={getCurrentColor(
                        colorMode,
                        "grayC.500",
                        "grayC.2200",
                      )}
                      focusBorderColor={"grayC.1300"}
                      color={getCurrentColor(
                        colorMode,
                        "blackC.200",
                        "whiteC.100",
                      )}
                      maxLength={255}
                      pl={8}
                      position="relative"
                      zIndex={1}
                    />
                    <EmailIcon
                      zIndex={2}
                      pointerEvents="none"
                      position="absolute"
                      top="50%"
                      left={2}
                      transform="translateY(-50%)"
                      color={getCurrentColor(
                        colorMode,
                        "grayC.200",
                        "grayC.1600",
                      )}
                    />
                  </Box>

                  {emailError && (
                    <Text color="red.500" fontSize="xs" mb={2}>
                      {emailError}
                    </Text>
                  )}

                  {subscribeStatus && subscribeStatus?.status !== "success" && (
                    <Flex alignItems="center" gap={2} mb={2}>
                      <CloseIcon color="red.500" />
                      <Text color="red.500" fontSize="sm">
                        {subscribeStatus?.message ||
                          "Could not subscribe. Please try again."}
                      </Text>
                    </Flex>
                  )}

                  <Button
                    type="submit"
                    w="100%"
                    bg={"blueC.400"}
                    color={"whiteC.100"}
                    _hover={{ bg: "blueC.200" }}
                    fontWeight="700"
                    isLoading={isSubmitting}
                  >
                    Sign Up Now <ArrowForwardIcon ml={1} />
                  </Button>
                </form>
              )}

              <Text
                fontSize="11px"
                color={getCurrentColor(colorMode, "grayC.200", "grayC.1600")}
                textAlign="center"
                mt={2}
              >
                We respect your privacy. Unsubscribe at any time.
              </Text>
            </Box>

            <Button
              onClick={handleDontShowAgain}
              variant="link"
              w="100%"
              color={getCurrentColor(colorMode, "grayC.200", "grayC.1600")}
              _hover={{
                color: getCurrentColor(colorMode, "blackC.200", "whiteC.100"),
              }}
              fontSize="sm"
            >
              Don&apos;t Show Me This Again
            </Button>
          </Box>
        </Box>
      )}

      {!isPopup && (
        <Box
          bg={getCurrentColor(colorMode, "whiteC.100", "gray.800")}
          borderRadius="lg"
          p={6}
          w="full"
          maxW="530px"
          position="relative"
          onClick={(e) => e.stopPropagation()}
          boxShadow="xl"
          overflow={"hidden"}
          rounded={"2xl"}
        >
          <Box
            position="absolute"
            top="0"
            right="0"
            w={40}
            h={40}
            bgGradient="linear(to-br, blue.500, purple.600)"
            borderRadius="full"
            opacity={0.2}
            transform="translateY(-50%) translateX(50%)"
            filter={"blur(30px)"}
          />
          <Box
            position="absolute"
            bottom="0"
            left="0"
            w={40}
            h={40}
            bgGradient="linear(to-tr, blue.600, cyan.400)"
            borderRadius="full"
            opacity={0.2}
            transform="translateY(50%) translateX(-50%)"
            filter={"blur(30px)"}
          />

          <Box textAlign="center">
            <Flex gap={4} mb={8}>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text
                  fontSize={{ base: "25px", md: "3xl" }}
                  fontWeight="bold"
                  color={getCurrentColor(colorMode, "blackC.200", "whiteC.100")}
                >
                  Sign up for our Newsletter
                </Text>
                <Text color={"blueC.100"} fontSize={{ base: "", md: "sm" }}>
                  Stay informed with Bullet Scout
                </Text>
              </Flex>
            </Flex>

            <Text
              color={getCurrentColor(colorMode, "blackC.200", "gray.300")}
              mb={4}
              textAlign={"start"}
              fontSize={{ base: "sm", md: "lg" }}
              lineHeight="1.5"
            >
              Subscribe to our newsletter and get the latest ammunition news,
              deals, and insights delivered directly to your inbox.
            </Text>

            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))"
              mb={6}
              gap={3}
            >
              <Tooltip
                label="Get instant notifications when ammunition prices drop below your specified thresholds."
                aria-label="Price drop alerts"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  color={getCurrentColor(colorMode, "blackC.200", "gray.300")}
                  fontSize="sm"
                >
                  <CheckIcon color="green.500" width={4} height={4} />
                  <Text ml={2}>Price drop alerts</Text>
                </Box>
              </Tooltip>

              <Tooltip
                label="Access special offers and discounts available only to newsletter subscribers."
                aria-label="Exclusive deals"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  color={getCurrentColor(colorMode, "blackC.200", "gray.300")}
                  fontSize="sm"
                >
                  <CheckIcon color="green.500" width={4} height={4} />
                  <Text ml={2}>Exclusive deals</Text>
                </Box>
              </Tooltip>

              <Tooltip
                label="Stay informed about ammunition restocks and availability."
                aria-label="Inventory updates"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  color={getCurrentColor(colorMode, "blackC.200", "gray.300")}
                  fontSize="sm"
                >
                  <CheckIcon color="green.500" width={4} height={4} />
                  <Text ml={2}>Inventory updates</Text>
                </Box>
              </Tooltip>

              <Tooltip
                label="Be the first to know about new ammunition products and brands."
                aria-label="New product releases"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  color={getCurrentColor(colorMode, "blackC.200", "gray.300")}
                  fontSize="sm"
                >
                  <CheckIcon color="green.500" width={4} height={4} />
                  <Text ml={2}>New product releases</Text>
                </Box>
              </Tooltip>

              <Tooltip
                label="Get updates on ammunition regulations and other industry news."
                aria-label="Industry news"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  color={getCurrentColor(colorMode, "blackC.200", "gray.300")}
                  fontSize="sm"
                >
                  <CheckIcon color="green.500" width={4} height={4} />
                  <Text ml={2}>Industry news</Text>
                </Box>
              </Tooltip>

              <Tooltip
                label="Get personalized ammunition suggestions based on your preferences."
                aria-label="Personalized recommendations"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  color={getCurrentColor(colorMode, "blackC.200", "gray.300")}
                  fontSize="sm"
                >
                  <CheckIcon color="green.500" width={4} height={4} />
                  <Text textAlign="start" ml={2}>
                    Personalized recommendations
                  </Text>
                </Box>
              </Tooltip>
            </Box>

            <form onSubmit={(e) => handleSubscribe(e, email, false)}>
              <FormControl id="email" mb={4} position="relative">
                <Input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={({ target }) => setEmail(target.value)}
                  required
                  bg={getCurrentColor(colorMode, "whiteC.100", "gray.700")}
                  borderColor={getCurrentColor(
                    colorMode,
                    "grayC.500",
                    "grayC.2200",
                  )}
                  paddingLeft={8}
                  position="relative"
                  zIndex={1}
                  _focus={{
                    borderColor: "blue.500",
                    ring: 1,
                    ringColor: "blue.500",
                  }}
                />
                <EmailIcon
                  zIndex={2}
                  pointerEvents="none"
                  position="absolute"
                  top="50%"
                  left={2}
                  transform="translate(0%, -50%)"
                  color="grayC.4000"
                />
              </FormControl>
              <Button
                type="submit"
                color="whiteC.100"
                bg="blueC.100"
                isLoading={isSubmitting}
                w="full"
                mb={4}
                rightIcon={<ArrowForwardIcon />}
              >
                Subscribe Now
              </Button>
            </form>

            <Text color="gray.500" fontSize="xs" textAlign="center" mb={6}>
              We respect your privacy. Unsubscribe at any time.
            </Text>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Newsletter;
